/**
 * Pure candidate-ranking logic for unlinked pathology results (no Angular).
 * Shared by the Web Worker and the synchronous main-thread fallback.
 *
 * CLINICAL SAFETY - read before changing anything in this file.
 *
 * This module RANKS SUGGESTIONS FOR A HUMAN. It never links, never pre-selects and never
 * submits. Nothing downstream may treat a row returned here as a decision.
 *
 * Identity documents are compared for EXACT equality only, after normalising case, spaces
 * and dashes. They are deliberately NOT routed through `compareStringsLevenshtein` /
 * `identityDocsMatch` from `patient-duplicate-match-core`: that comparator accepts a
 * distance of `Math.max(len1, len2) / 4`, i.e. three differing digits on a 13-digit South
 * African ID number, so two genuinely different patients compare as equal there. Levenshtein
 * is reused here for NAME similarity only, where slack is appropriate.
 */

import { compareStringsLevenshtein } from '../duplicate-match/patient-duplicate-match-core';

export type UnlinkedCandidateKind = 'exact-identity' | 'fuzzy';
export type UnlinkedCandidateBand = 'Exact' | 'High' | 'Medium' | 'Low';

/** Slim patient projection sent to the worker (structured-clone friendly). */
export interface UnlinkedMatchPatientPayload {
  id?: string;
  active?: boolean;
  status?: string;
  name?: string;
  surname?: string;
  patientNumber?: string;
  dob?: string;
  gender?: string;
  idNumber?: string;
  passportNumber?: string;
}

/** Demographics carried by the inbound result we are trying to place. */
export interface UnlinkedMatchSubject {
  firstName?: string | null;
  surname?: string | null;
  dateOfBirth?: string | null;
  sex?: string | null;
  idNumber?: string | null;
  passportNumber?: string | null;
}

export interface UnlinkedCandidateDetail {
  label: string;
  /** Value carried by the inbound result. */
  resultValue: string;
  /** Value on the patient file. */
  patientValue: string;
  /** True only for a character-for-character match. */
  exact: boolean;
}

export interface UnlinkedCandidateRow {
  kind: UnlinkedCandidateKind;
  patient: UnlinkedMatchPatientPayload;
  matchedLabels: string[];
  details: UnlinkedCandidateDetail[];
  /** Disagreements a human must see, e.g. "ID number differs". */
  conflicts: string[];
  /** 0-100. Ordering hint for a human. Never a threshold for an automated action. */
  score: number;
  band: UnlinkedCandidateBand;
}

const EM_DASH = '—';

/** Upper bound on fuzzy suggestions shown; exact identity matches are never truncated. */
export const MAX_FUZZY_CANDIDATES = 10;

// ---------------------------------------------------------------------------
// Identity documents - exact comparison only
// ---------------------------------------------------------------------------

/** Case, spaces and dashes are formatting; everything else is significant. */
export function normalizeIdentityDoc(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s\-]/g, '');
}

/**
 * An identity document either matches exactly (after normalisation) or it does not.
 * There is no "close enough" for an ID or passport number.
 */
export function identityDocsMatchExact(a: unknown, b: unknown): boolean {
  const na = normalizeIdentityDoc(a);
  const nb = normalizeIdentityDoc(b);
  if (!na || !nb) {
    return false;
  }
  return na === nb;
}

/** True when both sides carry a document and they disagree. */
export function identityDocsConflict(a: unknown, b: unknown): boolean {
  const na = normalizeIdentityDoc(a);
  const nb = normalizeIdentityDoc(b);
  if (!na || !nb) {
    return false;
  }
  return na !== nb;
}

// ---------------------------------------------------------------------------
// Dates of birth
// ---------------------------------------------------------------------------

interface DobParts { y: number; m: number; d: number; }

/** Accepts DD/MM/YYYY (in-memory patients), ISO YYYY-MM-DD[...] and YYYYMMDD (vendor payloads). */
export function parseDobParts(dob: unknown): DobParts | null {
  if (dob == null || dob === '') {
    return null;
  }
  const s = String(dob).trim();

  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (dmy) {
    const d = parseInt(dmy[1], 10);
    const m = parseInt(dmy[2], 10);
    const y = parseInt(dmy[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { y, m, d };
    }
  }

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const m = parseInt(iso[2], 10);
    const d = parseInt(iso[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { y, m, d };
    }
  }

  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (compact) {
    const y = parseInt(compact[1], 10);
    const m = parseInt(compact[2], 10);
    const d = parseInt(compact[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { y, m, d };
    }
  }

  return null;
}

function dobSameDay(a: unknown, b: unknown): boolean {
  const pa = parseDobParts(a);
  const pb = parseDobParts(b);
  return !!pa && !!pb && pa.y === pb.y && pa.m === pb.m && pa.d === pb.d;
}

function dobSameMonth(a: unknown, b: unknown): boolean {
  const pa = parseDobParts(a);
  const pb = parseDobParts(b);
  return !!pa && !!pb && pa.y === pb.y && pa.m === pb.m;
}

/** `M` / `Male` / `1` all normalise to `m`. */
function normalizeSex(value: unknown): string {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) {
    return '';
  }
  if (s.startsWith('m') || s === '1') {
    return 'm';
  }
  if (s.startsWith('f') || s === '2') {
    return 'f';
  }
  return s;
}

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function display(value: unknown): string {
  return text(value) || EM_DASH;
}

// ---------------------------------------------------------------------------
// Payload projection
// ---------------------------------------------------------------------------

export function toUnlinkedMatchPayload(p: any): UnlinkedMatchPatientPayload {
  if (p == null) {
    return {};
  }
  return {
    id: p.id,
    active: p.active,
    status: p.status,
    name: p.name,
    surname: p.surname,
    patientNumber: p.patientNumber,
    dob: p.dob != null ? String(p.dob) : undefined,
    gender: p.gender,
    idNumber: p.idNumber,
    passportNumber: p.passportNumber,
  };
}

export function toUnlinkedMatchSubject(demographics: any): UnlinkedMatchSubject {
  if (demographics == null) {
    return {};
  }
  return {
    firstName: demographics.firstName ?? null,
    surname: demographics.surname ?? null,
    dateOfBirth: demographics.dateOfBirth ?? null,
    sex: demographics.sex ?? null,
    idNumber: demographics.idNumber ?? null,
    passportNumber: demographics.passportNumber ?? null,
  };
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/** Weighted name/DOB/sex similarity. Deliberately excludes identity documents. */
const WEIGHT_SURNAME = 2;
const WEIGHT_FIRST_NAME = 2;
const WEIGHT_DOB_DAY = 3;
const WEIGHT_SEX = 1;

/** A fuzzy suggestion must at least agree on surname, and reach half the available weight. */
const FUZZY_MIN_RATIO = 0.5;

/** An ID number that disagrees halves the ranking of an otherwise plausible name match. */
const ID_CONFLICT_PENALTY = 0.5;

function scoreOne(
  subject: UnlinkedMatchSubject,
  patient: UnlinkedMatchPatientPayload,
): UnlinkedCandidateRow | null {
  const details: UnlinkedCandidateDetail[] = [];
  const matchedLabels: string[] = [];
  const conflicts: string[] = [];

  // --- Identity documents: exact or nothing ---
  const idExact = identityDocsMatchExact(subject.idNumber, patient.idNumber);
  const passportExact = identityDocsMatchExact(subject.passportNumber, patient.passportNumber);

  if (identityDocsConflict(subject.idNumber, patient.idNumber)) {
    conflicts.push('ID number differs');
  }
  if (identityDocsConflict(subject.passportNumber, patient.passportNumber)) {
    conflicts.push('Passport number differs');
  }

  if (idExact) {
    matchedLabels.push('ID number');
    details.push({
      label: 'ID number',
      resultValue: display(subject.idNumber),
      patientValue: display(patient.idNumber),
      exact: true,
    });
  }
  if (passportExact) {
    matchedLabels.push('Passport number');
    details.push({
      label: 'Passport number',
      resultValue: display(subject.passportNumber),
      patientValue: display(patient.passportNumber),
      exact: true,
    });
  }

  // --- Name / DOB / sex similarity (fuzzy is acceptable here) ---
  let score = 0;
  let maxScore = 0;
  let surnameMatched = false;

  const subjSurname = text(subject.surname);
  const patSurname = text(patient.surname);
  if (subjSurname && patSurname) {
    maxScore += WEIGHT_SURNAME;
    if (compareStringsLevenshtein(subjSurname, patSurname)) {
      score += WEIGHT_SURNAME;
      surnameMatched = true;
      matchedLabels.push('Surname');
      details.push({
        label: 'Surname',
        resultValue: subjSurname,
        patientValue: patSurname,
        exact: subjSurname.toLowerCase() === patSurname.toLowerCase(),
      });
    }
  }

  const subjFirst = text(subject.firstName);
  const patFirst = text(patient.name);
  if (subjFirst && patFirst) {
    maxScore += WEIGHT_FIRST_NAME;
    if (compareStringsLevenshtein(subjFirst, patFirst)) {
      score += WEIGHT_FIRST_NAME;
      matchedLabels.push('First name');
      details.push({
        label: 'First name',
        resultValue: subjFirst,
        patientValue: patFirst,
        exact: subjFirst.toLowerCase() === patFirst.toLowerCase(),
      });
    }
  }

  const subjDob = parseDobParts(subject.dateOfBirth);
  const patDob = parseDobParts(patient.dob);
  if (subjDob && patDob) {
    maxScore += WEIGHT_DOB_DAY;
    if (dobSameDay(subject.dateOfBirth, patient.dob)) {
      score += WEIGHT_DOB_DAY;
      matchedLabels.push('Date of birth');
      details.push({
        label: 'Date of birth',
        resultValue: display(subject.dateOfBirth),
        patientValue: display(patient.dob),
        exact: true,
      });
    } else if (dobSameMonth(subject.dateOfBirth, patient.dob)) {
      score += 1;
      matchedLabels.push('Date of birth (month)');
      details.push({
        label: 'Date of birth (same month)',
        resultValue: display(subject.dateOfBirth),
        patientValue: display(patient.dob),
        exact: false,
      });
    } else {
      conflicts.push('Date of birth differs');
    }
  }

  const subjSex = normalizeSex(subject.sex);
  const patSex = normalizeSex(patient.gender);
  if (subjSex && patSex) {
    maxScore += WEIGHT_SEX;
    if (subjSex === patSex) {
      score += WEIGHT_SEX;
      matchedLabels.push('Sex');
      details.push({
        label: 'Sex',
        resultValue: display(subject.sex),
        patientValue: display(patient.gender),
        exact: true,
      });
    } else {
      conflicts.push('Sex differs');
    }
  }

  const similarity = maxScore > 0 ? score / maxScore : 0;

  // An exact identity-document match is its own category and always outranks similarity.
  if (idExact || passportExact) {
    return {
      kind: 'exact-identity',
      patient,
      matchedLabels,
      details,
      conflicts,
      score: 100,
      band: 'Exact',
    };
  }

  if (!surnameMatched || similarity < FUZZY_MIN_RATIO) {
    return null;
  }

  let percent = Math.round(similarity * 100);
  if (conflicts.indexOf('ID number differs') !== -1) {
    percent = Math.round(percent * ID_CONFLICT_PENALTY);
  }
  // A fuzzy suggestion must never present itself as certain.
  percent = Math.min(percent, 99);

  return {
    kind: 'fuzzy',
    patient,
    matchedLabels,
    details,
    conflicts,
    score: percent,
    band: percent >= 85 ? 'High' : percent >= 65 ? 'Medium' : 'Low',
  };
}

/**
 * Ranks patients as candidates for one unlinked result.
 *
 * Exact identity-document matches come first and are labelled `exact-identity`;
 * fuzzy name/DOB suggestions follow, capped at `MAX_FUZZY_CANDIDATES`.
 * The caller must require an explicit human click before linking any of them.
 */
export function rankUnlinkedCandidatesCore(
  subject: UnlinkedMatchSubject | null | undefined,
  patients: UnlinkedMatchPatientPayload[] | null | undefined,
): UnlinkedCandidateRow[] {
  const subj = subject ?? {};
  const list = (patients ?? []).filter(p => !!p && p.active !== false);

  const exact: UnlinkedCandidateRow[] = [];
  const fuzzy: UnlinkedCandidateRow[] = [];

  for (const patient of list) {
    const row = scoreOne(subj, patient);
    if (!row) {
      continue;
    }
    if (row.kind === 'exact-identity') {
      exact.push(row);
    } else {
      fuzzy.push(row);
    }
  }

  const byName = (a: UnlinkedCandidateRow, b: UnlinkedCandidateRow) =>
    `${a.patient.surname ?? ''}${a.patient.name ?? ''}`.localeCompare(
      `${b.patient.surname ?? ''}${b.patient.name ?? ''}`
    );

  exact.sort(byName);
  fuzzy.sort((a, b) => (b.score - a.score) || byName(a, b));

  return [...exact, ...fuzzy.slice(0, MAX_FUZZY_CANDIDATES)];
}
